// Copyright 2018 Sorama B.V.

import {
    BindingEngine,
    Disposable,
    TaskQueue,
    autoinject,
    bindable,
    computedFrom,
    customElement,
    observable,
} from "aurelia-framework";
import { Point2D } from "datatypes/SoramaLocalApi";
import { Browser, MediaQueries } from "libraries/MediaQueries/MediaQueries";
import { IPlayablePropertiesService, PlayableProperty } from "services/PlayableProperties/IPlayablePropertiesService";
import { Time } from "units/Time";
import { UnitValue } from "units/Unit";
import * as WavEncoder from "wav-encoder";
import { AudioData } from "wav-encoder";

@customElement("audio-player")
@autoinject
export class AudioPlayer {
    private mediaQueries: MediaQueries;

    @observable
    public audioElement: HTMLAudioElement;

    private sliderElement: HTMLElement;
    private bindingEngine: BindingEngine;
    public playablePropertiesService: IPlayablePropertiesService;
    private taskQueue: TaskQueue;

    @bindable
    public audioElementSource: string;

    @bindable
    public minScale = -20;

    @bindable
    public maxScale = 20;

    @bindable
    public autoScale = false;

    @bindable
    public data: Point2D[];

    @bindable
    public samplerate: number;

    @bindable
    public name = "audio";

    @bindable
    public playableId: string;

    @bindable
    public autoPlay = false;

    private progress = 0;
    private error = false;
    private subscriptions: Disposable[] = [];
    private externalTimeObserver: Disposable[];
    private currentTime: UnitValue;

    @computedFrom("audioElement.duration", "data")
    private get duration(): number {
        if (this.data == undefined) return 0;

        return this.data.length / this.samplerate;
    }

    private get supported(): boolean {
        return this.mediaQueries.currentBrowser != Browser.InternetExplorer;
    }

    @computedFrom("audioElement.paused")
    private get isPlaying(): boolean {
        return this.audioElement && !this.audioElement.paused;
    }

    constructor(
        bindingEngine: BindingEngine,
        playablePropertiesService: IPlayablePropertiesService,
        mediaQueries: MediaQueries,
        taskQueue: TaskQueue
    ) {
        this.taskQueue = taskQueue;
        this.mediaQueries = mediaQueries;
        this.bindingEngine = bindingEngine;
        this.playablePropertiesService = playablePropertiesService;
    }

    public bind(): void {
        if (!this.supported) return;

        if (this.audioElement && this.data) {
            this.subscriptions = [
                this.bindingEngine
                    .propertyObserver(this.audioElement, "currentTime")
                    .subscribe(() => this.audioPlayerTimeChanged()),
                this.bindingEngine.collectionObserver(this.data).subscribe(this.dataChanged.bind(this)),
            ];
        }
        this.dataChanged();
    }

    public attached(): void {
        this.playableIdChanged(this.playableId, undefined);
    }

    public unbind(): void {
        while (this.subscriptions.length > 0) {
            this.subscriptions.pop().dispose();
        }

        if (this.externalTimeObserver) {
            while (this.externalTimeObserver.length > 0) this.externalTimeObserver.pop().dispose();
        }

        this.data = [];
    }

    public detached(): void {
        if (this.isPlaying) {
            this.stopPlaying();
        }
    }

    public audioElementChanged(): void {
        while (this.subscriptions.length > 0) {
            this.subscriptions.pop().dispose();
        }

        if (this.audioElement) {
            this.subscriptions = [
                this.bindingEngine
                    .propertyObserver(this.audioElement, "currentTime")
                    .subscribe(() => this.audioPlayerTimeChanged()),
            ];
        }
        if (this.audioElement && this.data) {
            this.subscriptions = [
                this.bindingEngine
                    .propertyObserver(this.audioElement, "currentTime")
                    .subscribe(() => this.audioPlayerTimeChanged()),
                this.bindingEngine.collectionObserver(this.data).subscribe(this.dataChanged.bind(this)),
            ];
        }
    }

    public currentTimeChanged(): void {
        // The time has changed from the outside world
        if (this.playableId && !this.isPlaying) {
            const newTime = this.playablePropertiesService.playableProperties[this.playableId].currentKey.toUnit(
                Time.second
            );

            if (this.currentTime && newTime === this.currentTime.toUnit(Time.second)) return;

            if (this.audioElement) {
                this.audioElement.currentTime = newTime;
            }
        } else if (this.playableId) {
            // If we're playing audio, make sure that the rate is always 1
            this.playablePropertiesService.playableProperties[this.playableId].playbackRate = 1.0;
        }
    }

    public audioPlayerTimeChanged(): void {
        // Propagate tot the service. Only update if the clock of the service is not running
        if (
            this.playableId &&
            !this.playablePropertiesService.playableProperties[this.playableId].isPlaying &&
            this.audioElement
        ) {
            this.currentTime = new UnitValue(this.audioElement.currentTime, Time.second);
            this.playablePropertiesService.playableProperties[this.playableId].currentKey = this.currentTime;
        }
    }
    public dataChanged = (): Promise<void> => {
        if (this.supported && this.data) {
            this.error = false;

            return this.dataToAudioBlob()
                .then((blob) => {
                    this.audioElementSource = URL.createObjectURL(blob);
                })
                .then(() => {
                    this.audioElement.load();
                })
                .catch((e) => {
                    console.log(e);
                    this.error = true;
                });
        }
    };

    public isPlayingChanged(): void {
        // Only a "Pause" can be triggered from the outside world.
        if (
            this.audioElement &&
            this.playableId &&
            !this.playablePropertiesService.playableProperties[this.playableId].isPlaying
        ) {
            this.audioElement.pause();
        }
    }

    public playableIdChanged(newValue: string, oldValue: string): void {
        if (newValue !== oldValue) {
            if (this.externalTimeObserver) {
                while (this.externalTimeObserver.length > 0) this.externalTimeObserver.pop().dispose();
            }

            if (newValue) {
                if (!this.playablePropertiesService.playableProperties[newValue]) {
                    // we need to create our own subscription
                    this.playablePropertiesService.register(
                        newValue,
                        new PlayableProperty(
                            new UnitValue(0, Time.second),
                            new UnitValue(this.duration, Time.second),
                            new UnitValue(1.0, Time.second)
                        )
                    );
                }

                this.externalTimeObserver = [
                    this.bindingEngine
                        .propertyObserver(this.playablePropertiesService.playableProperties[newValue], "currentKey")
                        .subscribe(() => {
                            this.currentTimeChanged();
                        }),
                    this.bindingEngine
                        .propertyObserver(this.playablePropertiesService.playableProperties[newValue], "isPlaying")
                        .subscribe(() => {
                            this.isPlayingChanged();
                        }),
                ];

                // propagate the new ids
                this.currentTimeChanged();
                if (!this.autoPlay) {
                    this.stopPlaying();
                }
            }
        }
    }

    public dataToAudioBlob = (): Promise<Blob> => {
        const data = new Float32Array(this.data.length);

        const audioData = <AudioData>{
            sampleRate: Number(this.samplerate),
            channelData: [data],
        };

        let scaleValue = 1.0;
        let mean = 0.0;

        if (this.autoScale || this.maxScale || this.minScale) {
            let min = Number.MAX_VALUE;
            let max = Number.MIN_VALUE;

            for (let i = 0; i < this.data.length; i++) {
                mean += this.data[i].y;
                max = this.data[i].y > max ? this.data[i].y : max;
                min = this.data[i].y < min ? this.data[i].y : min;
            }

            mean /= this.data.length;
            scaleValue = 1 / Math.max(Math.abs(min - mean), Math.abs(max - mean));
        } else {
            mean = (this.maxScale - this.minScale) / 2;
            scaleValue = 1 / Math.max(Math.abs(this.minScale - mean), Math.abs(this.maxScale - mean));
        }

        // Fill buffer
        for (let i = 0; i < data.length; i++) {
            // audio needs to be in [-1.0; 1.0] range
            data[i] = (this.data[i].y - mean) * scaleValue;
        }

        return WavEncoder.encode(audioData).then((x: ArrayBuffer) => {
            return new Blob([new Uint8Array(x)], { type: "audio/wav" });
        });
    };

    public startPlaying(): void {
        if (this.playableId) {
            this.playablePropertiesService.playableProperties[this.playableId].maxKey = new UnitValue(
                this.duration,
                Time.second
            );
            this.playablePropertiesService.playableProperties[this.playableId].playbackRate = 1.0;

            if (this.playablePropertiesService.playableProperties[this.playableId].isPlaying) {
                this.playablePropertiesService.pause(this.playableId); // Trigger other audio elements to stop playing.
                this.taskQueue.queueTask(() => {
                    // start playing when all subscribed elements have paused
                    this.playablePropertiesService.play(this.playableId);
                    this.audioElement.volume = 1.0;
                    this.audioElement.play();
                });
                return;
            } else {
                this.playablePropertiesService.play(this.playableId);
            }
        }

        this.audioElement.volume = 1.0;
        this.audioElement.play();
    }

    public stopPlaying(): void {
        if (this.playableId) {
            this.playablePropertiesService.pause(this.playableId);
        }

        if (this.audioElement) {
            this.audioElement.pause();
        }
    }

    protected mouseMove(e: MouseEvent): void {
        // Left mouse button
        if (e.buttons % 2 === 1) {
            const rect = this.sliderElement.getBoundingClientRect();
            let val = ((e.pageX - rect.left) / this.sliderElement.clientWidth) * this.duration;
            val = Math.max(0, val);
            val = Math.min(val, this.duration);
            this.audioElement.currentTime = val;
        }
    }
}
